import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import { $hasFeature } from '@/core/utils/VueAcl'
import { SubscriptionPreferenceChannelLabels } from '@/modules/settings/enum/SubscriptionPreferenceChannelEnum'

export default {
  data() {
    return {
      subscriptionPreferences: {},
      preferenceMetadata: {},
      customChannels: []
    }
  },
  computed: {
    subscriptionPreferencesEnabled() {
      return $hasFeature('subscriptionPreferences')
    },
    consentPreferences() {
      const preferences = {}
      const attributes = this.getAttributes()
      if (!attributes || typeof attributes !== 'object') {
        return preferences
      }
      Object.keys(attributes).forEach(attr => {
        if (this.isConsentPreference(attr)) {
          preferences[attr] = attributes[attr]
        }
      })
      return preferences
    },
    consentPreferencesCount() {
      return Object.keys(this.consentPreferences).length
    }
  },
  methods: {
    $hasFeature,
    isConsentPreference(attribute) {
      if (!attribute || typeof attribute !== 'string') {
        return false
      }
      return attribute.includes('user.preferences')
    },
    parseConsentPreferenceKey(attribute) {
      if (!attribute || typeof attribute !== 'string') {
        return null
      }

      const patterns = [
        /user\.preferences\[['"]([^'"]*)['"]\]\s*\[['"]([^'"]+)['"]\]/,
        /preferences\[['"]([^'"]*)['"]\]\s*\[['"]([^'"]+)['"]\]/,
        /user\.preferences\.([^.]*)\.([^.]+)/,
        /preferences\.([^.]*)\.([^.]+)/
      ]

      for (const pattern of patterns) {
        const match = attribute.match(pattern)
        if (match) {
          return {
            channel: match[1] || '',
            name: match[2]
          }
        }
      }

      return null
    },
    fetchSubscriptionPreferences(profileId) {
      if (!profileId) {
        return
      }

      const channels = new Set()
      Object.keys(this.consentPreferences).forEach(attr => {
        const parsed = this.parseConsentPreferenceKey(attr)
        if (parsed && parsed.channel !== undefined) {
          channels.add(parsed.channel)
        }
      })

      const promises = Array.from(channels).map(channel => {
        return RestApi(
          projectEndpoint({
            method: 'get',
            url: `profiles/${profileId}/subscription-preferences`,
            params: {
              channel: channel,
              profile_id: profileId
            }
          })
        )
          .then(({ data }) => {
            if (!data || !Array.isArray(data)) return
            if (!this.subscriptionPreferences[channel]) {
              this.$set(this.subscriptionPreferences, channel, {})
              this.$set(this.preferenceMetadata, channel, {})
            }
            data.forEach(pref => {
              if (!pref.name) return
              const status = pref.status !== undefined ? pref.status : null
              this.$set(
                this.subscriptionPreferences[channel],
                pref.name,
                status
              )
              this.$set(this.preferenceMetadata[channel], pref.name, {
                display_name:
                  pref.display_name !== undefined ? pref.display_name : null,
                channel: pref.channel || channel
              })
              this.syncPreferenceToAttributes(channel, pref.name, status)
            })
          })
          .catch(error => {
            console.warn(
              `Failed to fetch subscription preferences for channel ${channel}:`,
              error
            )
          })
      })

      Promise.all(promises).then(() => {
        this.onPreferencesFetched()
      })
    },
    getConsentPreferenceLabel(attribute) {
      if (!this.isConsentPreference(attribute)) {
        return attribute
      }

      const parsed = this.parseConsentPreferenceKey(attribute)
      if (!parsed) {
        return attribute
      }

      const channel = parsed.channel || ''
      const name = parsed.name
      const channelMetadata = this.preferenceMetadata[channel]
      const metadata = channelMetadata && channelMetadata[name]

      const displayName =
        (metadata &&
          metadata.display_name &&
          typeof metadata.display_name === 'string' &&
          metadata.display_name.trim()) ||
        name
      const prefChannel =
        metadata && metadata.channel !== undefined ? metadata.channel : channel

      const channelLabel = this.getChannelLabel(prefChannel)
      return `${channelLabel} - ${displayName}`
    },
    fetchCustomChannels() {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `consent-channels`,
          params: {
            sort: 'name'
          }
        })
      )
        .then(({ data }) => {
          this.customChannels = data || []
        })
        .catch(error => {
          console.warn('Failed to fetch custom channels:', error)
        })
    },
    getChannelLabel(channelKey) {
      if (!channelKey || !channelKey.trim()) {
        return this.$t('CampaignForm_Preview_Profile_NoChannel')
      }

      if (SubscriptionPreferenceChannelLabels[channelKey]) {
        return this.$t(SubscriptionPreferenceChannelLabels[channelKey])
      }

      const customChannel = this.customChannels.find(
        ch => ch.key === channelKey
      )
      if (customChannel && customChannel.name) {
        return customChannel.name
      }

      return channelKey
    },
    getAttributes() {
      throw new Error('getAttributes() must be implemented by component')
    },
    syncPreferenceToAttributes(channel, name, status) {},
    onPreferencesFetched() {}
  }
}



// WEBPACK FOOTER //
// ./src/modules/campaign-manage/components/preview/content-data-tabs/mixins/SubscriptionPreferencesMixin.js